import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API, getOrCreateUserId } from "../lib/api";
import { sfx } from "../lib/sound";

/**
 * Ad-Free upsell — one-time celebratory modal triggered when the user has
 * watched a meaningful number of ads. Industry-best converting moment in F2P:
 * users who've watched ≥50 ads have demonstrated value perception of the
 * product, but they're also feeling the friction.
 *
 * Trigger logic (pure UI):
 *   - Profile.ads_watched_total >= 50
 *   - Profile.ad_free_upsell_shown is FALSE
 *   - Profile.ad_free_until is null/expired (already-bought users skip)
 *
 * On dismiss OR purchase, POST /api/profile/{uid}/ad-upsell-shown to flip
 * the flag so it never re-appears.
 */
const AD_THRESHOLD = 50;

export default function AdFreeUpsellModal({ profile, onPurchase }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!profile) return;
    const watched = profile.ads_watched_total || 0;
    const alreadyShown = !!profile.ad_free_upsell_shown;
    const alreadyAdFree = !!profile.ad_free_until && new Date(profile.ad_free_until) > new Date();
    if (watched >= AD_THRESHOLD && !alreadyShown && !alreadyAdFree) {
      // Slight delay so the user isn't ambushed mid-action
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, [profile]);

  const flagShown = async () => {
    try {
      const uid = getOrCreateUserId();
      await axios.post(`${API}/profile/${uid}/ad-upsell-shown`);
    } catch (e) {
      // Non-fatal — worst case the user sees this modal once more later
      console.warn("ad-upsell-shown flag failed:", e);
    }
  };

  const dismiss = async () => {
    sfx.click();
    setOpen(false);
    await flagShown();
  };

  const buy = async () => {
    sfx.click();
    setOpen(false);
    await flagShown();
    onPurchase?.();
  };

  if (!profile) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[160] flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.82)" }}
          onClick={dismiss}
          data-testid="ad-free-upsell-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 16 }} animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="glass rounded-3xl p-6 w-full"
            style={{
              maxWidth: 380,
              border: "1px solid rgba(0,229,255,0.55)",
              boxShadow: "0 0 32px rgba(0,229,255,0.30)",
            }}
          >
            <div className="text-center mb-4">
              <div style={{ fontSize: 42 }} className="mb-2">🎉</div>
              <div className="label-eyebrow mb-1" style={{ color: "#00E5FF" }}>
                YOU'VE WATCHED {profile.ads_watched_total} ADS
              </div>
              <h2 className="font-display font-black text-2xl mb-2">
                Ready for an upgrade?
              </h2>
              <p className="text-white/65 text-sm">
                You've watched a lot of ads. Get <b style={{ color: "#00E5FF" }}>Ad-Free Quattro</b> and
                save ~{Math.round((profile.ads_watched_total * 18) / 60)} minutes of your time per week.
              </p>
            </div>
            <div
              className="rounded-2xl p-4 mb-4 text-center"
              style={{
                background: "linear-gradient(135deg, rgba(0,229,255,0.12), rgba(181,55,242,0.10))",
                border: "1px solid rgba(0,229,255,0.40)",
              }}
            >
              <div className="font-display font-black text-3xl mb-1" style={{ color: "#00E5FF" }}>
                $4.99
              </div>
              <div className="text-white/70 text-xs">
                One-time. Lifetime ad-free. No more banners. No more interstitials.
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={dismiss}
                className="btn-ghost flex-1"
                data-testid="ad-free-upsell-dismiss"
              >
                Not now
              </button>
              <button
                onClick={buy}
                className="btn-primary flex-1"
                data-testid="ad-free-upsell-buy"
                style={{
                  background: "linear-gradient(135deg, #00E5FF, #B537F2)",
                  color: "#0B0C10",
                  fontWeight: 800,
                }}
              >
                Get Ad-Free
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
